import React from 'react';
import styles from '../../styles/components/answers/TextFieldGrid.module.css';
import { Grid, HeaderData } from './layouts/Grid';
import { NumericTextField } from './NumericTextField';
import { BaseNumericValidation } from '../../helpers/validationBuilder';
import _ from 'lodash';
import UUID from '@ax2/common/model/UUID';
import {
  ParticipantDefinedField,
  ParticipantDefinedFieldData,
} from './ParticipantDefinedOption';
import { MobileGrid } from './layouts/MobileGrid';
import { NumericTextFieldList } from './NumericTextFieldList';

export interface NumericTextFieldGridColOption {
  id: UUID;
  title: string;
  subtitle?: string;
}
export interface NumericTextFieldGridRowOption {
  id: UUID;
  title: string;
  subtitle?: string;
}

export type NumericTextFieldGridParticipantDefinedOption = ParticipantDefinedFieldData;

export interface NumericTextFieldGridOption {
  colId: UUID;
  rowId: UUID;
  value: number | null;
  error?: string;
}

export interface NumericTextFieldGridData {
  cols: NumericTextFieldGridColOption[];
  rows: (
    | NumericTextFieldGridRowOption
    | NumericTextFieldGridParticipantDefinedOption)[];
  isDesktop: boolean;
  validation: BaseNumericValidation;
  preselected?: NumericTextFieldGridOption[];
  onCompletion: (opts: NumericTextFieldGridOption[]) => void;
  key?: string;
}

function getInitialState(
  data: NumericTextFieldGridData
): NumericTextFieldGridOption[][] {
  const state = _.map(data.cols, col => {
    return _.map(data.rows, row => {
      const preOpt = _.find(
        data.preselected,
        p => p.colId === col.id && p.rowId === row.id
      );
      const opt: NumericTextFieldGridOption = preOpt
        ? preOpt
        : {
            colId: col.id,
            rowId: row.id,
            value: null,
          };
      return opt;
    });
  });
  return state;
}
function getNormalizedData(
  data: NumericTextFieldGridOption[][]
): NumericTextFieldGridOption[] {
  return _.filter(_.flatten(data), d => !!d.value || !!d.error);
}

export const NumericTextFieldGrid: React.FC<
  NumericTextFieldGridData
> = data => {
  return data.isDesktop ? (
    <NumericTextFieldGridDesktop {...data} />
  ) : (
    <NumericTextFieldGridMobile {...data} />
  );
};

const NumericTextFieldGridDesktop: React.FC<
  NumericTextFieldGridData
> = data => {
  const [inputsData, setInputsData] = React.useState<
    NumericTextFieldGridOption[][]
  >(getInitialState(data));
  const errorOpt = _.find(getNormalizedData(inputsData), d => !!d.error);
  const cols: HeaderData[] = data.cols.map(c => c.title);
  const rows: HeaderData[] = data.rows.map(r =>
    'title' in r ? r.title : <ParticipantDefinedField {...r} />
  );
  return (
    <div>
      <Grid
        cols={cols}
        rows={rows}
        elementRenderer={(colIndex, rowIndex) => {
          const cellData = inputsData[colIndex][rowIndex];
          return (
            <NumericTextField
              tabIndex={1 + colIndex + rowIndex * cols.length}
              validation={data.validation}
              value={cellData.value}
              error={cellData.error ? cellData.error : null}
              onCompletion={(v, e) => {
                const newInputsData = [...inputsData];
                newInputsData[colIndex][rowIndex].value = v;
                newInputsData[colIndex][rowIndex].error = e ? e : undefined;
                setInputsData(newInputsData);
                data.onCompletion(getNormalizedData(newInputsData));
              }}
            />
          );
        }}
      />
      {errorOpt && (
        <div className={styles.error}>
          <label>{errorOpt.error}</label>
        </div>
      )}
    </div>
  );
};

const NumericTextFieldGridMobile: React.FC<NumericTextFieldGridData> = data => {
  return (
    <MobileGrid
      primaryOptions={data.cols}
      secondaryRenderer={(primaryOptionId, _primaryOptionIndex) => (
        <NumericTextFieldList
          validation={data.validation}
          isPercentageMode={false}
          options={_.map(data.rows, r => {
            const value = _.find(
              data.preselected,
              p => p.colId === primaryOptionId && p.rowId === r.id
            );
            return {
              id: r.id,
              label: 'title' in r ? r.title : '', //TODO: we should handle other/stecify case too
              value: value ? value.value : null,
              error: value ? value.error : undefined,
            };
          })}
          onCompletion={selection => {
            const oldData = _.filter(
              data.preselected,
              p => p.colId !== primaryOptionId
            ); //Old selection without primary section
            const newData = _.map(selection, s => ({
              colId: primaryOptionId,
              rowId: s.id,
              value: s.value,
              error: s.error,
            }));
            data.onCompletion(_.concat(oldData, newData));
          }}
          key={primaryOptionId}
        />
      )}
    />
  );
};
